/**
 * Client-side fuzzy search over the header site selector's options.
 *
 * The expensive half of scoring (normalising every searchable field of every
 * option and tokenising it) does not depend on the query, so it is done once in
 * `buildSiteSearchIndex` and reused by `searchSiteIndex` on every keystroke.
 * The one-shot helpers at the bottom keep the original API for callers that
 * only search once.
 */

/** Option keys that take part in matching, in priority order. */
const SEARCH_FIELDS = [
	"searchTokens",
	"text",
	"value",
	"customer_site_name",
	"customer_site_number",
	"parent_customer_name",
	"parent_customer_number",
	"customer_name",
	"customer_number",
	"site_name",
	"warehouse",
	"site_legal_entity",
];

/**
 * Lower-cases a value, folds diacritics and collapses every run of
 * non-alphanumerics into a single space.
 *
 * Diacritics are folded so a CRM spelling such as "Günther" still matches a
 * user typing "Gunther" or "Guenther" (the latter via the fuzzy token pass);
 * without folding the "ü" is dropped and "g nther" can never match.
 *
 * @param {*} value Any value; non-strings are stringified, null/undefined become "".
 * @returns {string} Normalised, trimmed text.
 */
const normalizeSearchText = (value = "") =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, " ")
		.trim();

/**
 * Removes the spaces from already-normalised text so "c h guenther" and
 * "chguenther" compare equal.
 *
 * @param {string} normalized Output of `normalizeSearchText`.
 * @returns {string} The text without spaces.
 */
const compact = (normalized) => normalized.replace(/ /g, "");

/**
 * Levenshtein distance between two strings (two-row dynamic programming).
 *
 * @param {string} left First string.
 * @param {string} right Second string.
 * @returns {number} Minimum number of single-character edits.
 */
const getEditDistance = (left = "", right = "") => {
	if (left === right) return 0;
	if (!left.length) return right.length;
	if (!right.length) return left.length;

	let previous = Array.from({ length: right.length + 1 }, (_, i) => i);
	let current = new Array(right.length + 1).fill(0);

	for (let l = 1; l <= left.length; l += 1) {
		current[0] = l;
		for (let r = 1; r <= right.length; r += 1) {
			const substitutionCost = left[l - 1] === right[r - 1] ? 0 : 1;
			current[r] = Math.min(current[r - 1] + 1, previous[r] + 1, previous[r - 1] + substitutionCost);
		}
		[previous, current] = [current, previous];
	}

	return previous[right.length];
};

/**
 * Scores one query token against one candidate token.
 *
 * Exact match beats prefix beats substring beats a typo-tolerant edit-distance
 * match. Typo tolerance only kicks in for tokens of 4+ characters and a length
 * gap of at most 2, otherwise short tokens match almost everything.
 *
 * @param {string} queryToken Normalised token typed by the user.
 * @param {string} candidateToken Normalised token from an option.
 * @returns {number} 0 for no match, otherwise a score in the 55-120 range.
 */
const getTokenFuzzyScore = (queryToken = "", candidateToken = "") => {
	if (!queryToken || !candidateToken) return 0;
	if (candidateToken === queryToken) return 120;
	if (candidateToken.startsWith(queryToken)) return 90;
	if (queryToken.length >= 3 && candidateToken.includes(queryToken)) return 70;

	const lengthGap = Math.abs(candidateToken.length - queryToken.length);
	if (queryToken.length < 4 || lengthGap > 2) return 0;

	const maxDistance = queryToken.length >= 7 ? 2 : 1;
	if (getEditDistance(queryToken, candidateToken) > maxDistance) return 0;

	return maxDistance === 1 ? 60 : 55;
};

/**
 * Pre-computes the query-independent part of scoring for one option.
 *
 * @param {object} option A site option (any subset of `SEARCH_FIELDS` may be present).
 * @param {number} index Position in the source list; used as a stable tie-breaker.
 * @returns {{option: object, index: number, combinedText: string, compactCombined: string, candidateTokens: string[]}}
 */
const buildSiteSearchEntry = (option = {}, index = 0) => {
	const normalizedFields = SEARCH_FIELDS.map((key) => normalizeSearchText(option[key])).filter(Boolean);
	const combinedText = normalizedFields.join(" ");
	const candidateTokens = Array.from(
		new Set(
			normalizedFields.flatMap((field) => {
				const tokens = field.split(" ");
				const compactField = compact(field);
				return compactField ? [...tokens, compactField] : tokens;
			})
		)
	);
	return { option, index, combinedText, compactCombined: compact(combinedText), candidateTokens };
};

/**
 * Builds a reusable search index for a list of site options. Memoise the result
 * on the options array and pass it to `searchSiteIndex` per keystroke.
 *
 * @param {object[]} siteOptions Site options as produced by `siteContext`.
 * @returns {object[]} Index entries in the same order as `siteOptions`.
 */
const buildSiteSearchIndex = (siteOptions = []) => siteOptions.map(buildSiteSearchEntry);

/**
 * Normalises a raw query once so it can be scored against many entries.
 *
 * @param {string} rawQuery Text typed by the user.
 * @returns {{normalizedQuery: string, compactQuery: string, queryTokens: string[]}}
 */
const prepareQuery = (rawQuery = "") => {
	const normalizedQuery = normalizeSearchText(rawQuery);
	return {
		normalizedQuery,
		compactQuery: compact(normalizedQuery),
		queryTokens: normalizedQuery.split(" ").filter(Boolean),
	};
};

/**
 * Scores one index entry against a prepared query.
 *
 * Whole-string matches (exact / prefix / substring) and space-insensitive
 * matches add to the score; then every query token must fuzzy-match some
 * candidate token, unless the space-insensitive match already succeeded.
 *
 * @param {object} entry Output of `buildSiteSearchEntry`.
 * @param {object} query Output of `prepareQuery`.
 * @returns {number} 0 when the entry does not match, otherwise a positive relevance score.
 */
const scoreEntry = (entry, { normalizedQuery, compactQuery, queryTokens }) => {
	const { combinedText, compactCombined, candidateTokens } = entry;
	if (!normalizedQuery || !combinedText) return 0;

	let score = 0;
	if (combinedText === normalizedQuery) score += 500;
	else if (combinedText.startsWith(normalizedQuery)) score += 220;
	else if (combinedText.includes(normalizedQuery)) score += 160;

	const compactHit = compactQuery.length >= 4 && compactCombined.includes(compactQuery);
	if (compactHit) score += compactCombined.startsWith(compactQuery) ? 120 : 80;

	let matchedTokens = 0;
	for (const queryToken of queryTokens) {
		let best = 0;
		for (const candidate of candidateTokens) {
			best = Math.max(best, getTokenFuzzyScore(queryToken, candidate));
			if (best === 120) break; // exact token match is the ceiling
		}
		if (best > 0) {
			matchedTokens += 1;
			score += best;
		}
	}

	if (matchedTokens !== queryTokens.length) return compactHit ? score : 0;
	return score + 40;
};

/**
 * Searches a pre-built index and returns matching options, best match first.
 *
 * @param {object[]} index Output of `buildSiteSearchIndex`.
 * @param {string} query Text typed by the user; blank returns every option unranked.
 * @returns {object[]} Matching site options ordered by descending score, then source order.
 */
const searchSiteIndex = (index = [], query = "") => {
	const prepared = prepareQuery(query);
	if (!prepared.normalizedQuery) return index.map((entry) => entry.option);

	return index
		.map((entry) => ({ entry, score: scoreEntry(entry, prepared) }))
		.filter(({ score }) => score > 0)
		.sort((a, b) => b.score - a.score || a.entry.index - b.entry.index)
		.map(({ entry }) => entry.option);
};

/**
 * One-shot score for a single option; convenience wrapper over the index path.
 *
 * @param {object} option A site option.
 * @param {string} rawQuery Text typed by the user.
 * @returns {number} 0 when it does not match, otherwise the relevance score.
 */
const getSiteOptionSearchScore = (option = {}, rawQuery = "") =>
	scoreEntry(buildSiteSearchEntry(option), prepareQuery(rawQuery));

/**
 * One-shot filter for callers that search once; builds a throw-away index.
 *
 * @param {object[]} siteOptions Site options.
 * @param {string} query Text typed by the user.
 * @returns {object[]} Matching options, best match first.
 */
const getFilteredSiteOptions = (siteOptions = [], query = "") =>
	searchSiteIndex(buildSiteSearchIndex(siteOptions), query);

export {
	normalizeSearchText,
	getSiteOptionSearchScore,
	getFilteredSiteOptions,
	buildSiteSearchIndex,
	searchSiteIndex,
};
